import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, inject, signal} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {SpinnerModule} from "@niceltd/sol-angular/spinner";
import {GridApi} from "ag-grid-community";

/**
 * Params an AG Grid loading overlay receives (`loadingOverlayComponentParams`
 * are merged in). `skeleton` is asked each time the overlay opens: true shows
 * skeleton rows (first load of a list grid), false the Lyra spinner.
 */
export interface EspGridLoadingParams {
  api?: GridApi;
  skeleton?: () => boolean;
}

interface SkeletonColumn {
  /** Pixel width; null shares the row evenly (grid has no columns yet). */
  width: number | null;
  /** Narrow columns (selection checkbox, row actions) get a square block. */
  square: boolean;
}

// Bar lengths (% of the cell) cycled across rows and columns, so the skeleton
// reads as text of varying length rather than a grid of identical blocks.
const BAR_WIDTHS = [72, 48, 60, 84, 40, 66];
const SQUARE_COLUMN_MAX_WIDTH = 56;
const FALLBACK_ROW_COUNT = 10;
// Grids that build their columns from the loaded data (historical data) have
// none yet: show this many evenly spaced bars instead.
const FALLBACK_COLUMN_COUNT = 6;

/**
 * Loading state for ESP grids and panels.
 *
 * - Default: the SOL spinner (`sol-spinner`, recoloured to the Lyra primary in
 *   src/assets/styles/loading.scss).
 * - Inside an AG Grid loading overlay whose `skeleton()` param returns true:
 *   Lyra skeleton rows laid out under the grid's real header, one bar per
 *   displayed column (src/assets/styles/loading.scss lets the overlay fill the
 *   grid instead of centring it).
 *
 * Standalone and free of ESP's translate pipe, so the overlay components that
 * render it only need this component in their TestBed imports.
 */
@Component({
  selector: "esp-grid-loading",
  imports: [SpinnerModule],
  template: `
    @if (skeleton) {
      <div class="esp-grid-skeleton" role="status" [attr.aria-label]="label" [style.padding-top.px]="headerHeight()">
        @for (row of rows(); track $index) {
          <div class="esp-grid-skeleton-row" [style.height.px]="rowHeight()">
            @for (cell of row; track $index) {
              <div class="esp-grid-skeleton-cell" [class.is-flexible]="cell.width === null" [style.width.px]="cell.width">
                <span class="esp-grid-skeleton-bar" [class.is-square]="cell.square" [style.width.%]="cell.square ? null : cell.bar"></span>
              </div>
            }
          </div>
        }
      </div>
    } @else {
      <sol-spinner [showSpinner]="true" [ariaLabel]="label"></sol-spinner>
    }
  `,
  styleUrls: ["./grid-loading.component.scss"],
  host: {"[class.is-skeleton]": "skeleton"},
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EspGridLoadingComponent {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly translate = inject(TranslateService, {optional: true});
  private readonly cd = inject(ChangeDetectorRef);

  /** The overlay's AG Grid params; omit outside a grid (spinner only). */
  @Input() set params(value: EspGridLoadingParams | null | undefined) {
    this.gridParams = value ?? undefined;
    const skeleton = !!value?.api && !!value.skeleton?.();
    if (skeleton && !this.skeleton) {
      // AG Grid attaches the overlay's element after creating it, so measure
      // the grid on the next task, once the overlay is in the document. (Not
      // requestAnimationFrame: it never fires in a background tab.)
      setTimeout(() => this.layoutSkeleton());
    }
    this.skeleton = skeleton;
  }

  skeleton = false;
  readonly label = this.loadingLabel();
  readonly headerHeight = signal(0);
  readonly rowHeight = signal(36);
  readonly rows = signal<Array<Array<SkeletonColumn & {bar: number}>>>([]);

  private gridParams?: EspGridLoadingParams;

  private layoutSkeleton(): void {
    const api = this.gridParams?.api;
    if (!api || api.isDestroyed?.()) {
      return;
    }
    const root = this.el.nativeElement.closest(".ag-root-wrapper");
    const header = root?.querySelector<HTMLElement>(".ag-header");
    const headerHeight = header?.offsetHeight ?? 0;
    const rowHeight = api.getGridOption("rowHeight") || this.rowHeight();
    const bodyHeight = (root?.clientHeight ?? 0) - headerHeight;
    const rowCount = bodyHeight > 0 ? Math.ceil(bodyHeight / rowHeight) : FALLBACK_ROW_COUNT;

    let columns: SkeletonColumn[] = api.getAllDisplayedColumns().map(column => {
      const width = column.getActualWidth();
      return {width, square: width <= SQUARE_COLUMN_MAX_WIDTH};
    });
    if (!columns.length) {
      columns = Array.from({length: FALLBACK_COLUMN_COUNT}, () => ({width: null, square: false}));
    }

    this.headerHeight.set(headerHeight);
    this.rowHeight.set(rowHeight);
    this.rows.set(Array.from({length: rowCount}, (_, r) =>
      columns.map((column, c) => ({...column, bar: BAR_WIDTHS[(r + c) % BAR_WIDTHS.length]}))
    ));
    this.cd.markForCheck();
  }

  private loadingLabel(): string {
    const translated = this.translate?.instant("plan.loading");
    return translated && translated !== "plan.loading" ? translated : "Loading";
  }
}
